// pro.rejouis.fr — espace des boutiques partenaires.
// Une boutique suit chaque lot qu'elle envoie (reçus → reconditionnés → revendus),
// déclare un nouvel envoi avec une étiquette Mondial Relay par carton, et voit son bon
// d'achat si la rétrocommission est active. Routes : /, /declarer, /envoi/<ref>, /callback.
import { useEffect, useState, useCallback } from 'react';
import { login, handleCallback, compteConnecte, autreAdresse, reprendreReconnexion, ouvrirApercu, logout } from './lib/auth.js';
import { pro, reduirePhoto, fmtDate, fmtEuro, taux } from './lib/api.js';
import { Icons, Spinner, Logo, Jauge, Retour } from './components/ui.jsx';
import { naviguer } from './lib/nav.js';
import { Commander, DetailCommande, CarteCommande } from './Commandes.jsx';
import Faq from './Faq.jsx';
import BonAchat from './BonAchat.jsx';
import Coordonnees, { resumeAdresse } from './Coordonnees.jsx';

const LARGEUR = 760;
const CARTE_RELAIS = 'https://www.mondialrelay.fr/trouver-le-point-relais-le-plus-proche-de-chez-moi/';
const STATUTS = {
  'Déclaré': { label: 'À expédier', badge: 'badge-creme' },
  'En transit': { label: 'En route', badge: 'badge-creme' },
  'Reçu': { label: 'Reçu à l’atelier', badge: 'badge-mint' },
  'Traité': { label: 'Traité', badge: 'badge-stone' },
};
const aExpedier = (e) => e.statut === 'Déclaré' || e.statut === 'En transit';
// Suivi Mondial Relay du carton (lab, _envoisSuivi.js), étapes 1 à 4.
const ETAPES_COLIS = ['', 'Déposé en point relais', 'En route vers l’atelier', 'Arrivé près de l’atelier', 'Arrivé à l’atelier'];


export default function App() {
  const [chemin, setChemin] = useState(window.location.pathname);
  const [compte, setCompte] = useState(() => compteConnecte());
  const [erreur, setErreur] = useState(null);
  const [refus, setRefus] = useState(null);
  const [tableau, setTableau] = useState(null);
  // Retour de la déconnexion Shopify (« autre adresse e-mail ») : on relance la connexion.
  const [reconnexion] = useState(() => reprendreReconnexion());

  useEffect(() => {
    const maj = () => setChemin(window.location.pathname);
    window.addEventListener('popstate', maj);
    return () => window.removeEventListener('popstate', maj);
  }, []);

  // Retour de Shopify.
  useEffect(() => {
    if (chemin !== '/callback') return;
    handleCallback()
      .then((c) => { setCompte(c); window.history.replaceState({}, '', '/'); setChemin('/'); })
      .catch((e) => {
        window.history.replaceState({}, '', '/'); setChemin('/');
        if (e.message === 'acces_refuse') setRefus(e.email || '');
        else setErreur(e.message);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Aperçu staff ouvert depuis le lab (fiche partenaire).
  useEffect(() => {
    if (chemin !== '/apercu') return;
    ouvrirApercu()
      .then((c) => { setTableau(null); setCompte(c); window.history.replaceState({}, '', '/'); setChemin('/'); })
      .catch((e) => { window.history.replaceState({}, '', '/'); setChemin('/'); setErreur(e.message); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const charger = useCallback(async () => {
    try { setTableau(await pro('tableau')); }
    catch (e) { setErreur(e.message); }
  }, []);
  useEffect(() => { if (compte) charger(); }, [compte, charger]);

  let ecran;
  if (chemin === '/callback' || chemin === '/apercu' || reconnexion) ecran = <Centre><Spinner size={22} label="Connexion…" /></Centre>;
  else if (refus !== null) ecran = <AccesRefuse email={refus} onRetour={() => setRefus(null)} />;
  else if (!compte) ecran = <Connexion erreur={erreur} />;
  else if (!tableau) ecran = erreur ? <Centre><div className="t-stone">{erreur}</div></Centre> : <Centre><Spinner size={22} label="Chargement de vos envois…" /></Centre>;
  else if (chemin === '/declarer') ecran = <Declarer onFait={async (ref) => { await charger(); naviguer(`/envoi/${encodeURIComponent(ref)}`); }} />;
  else if (chemin === '/bon-achat') ecran = <BonAchat partenaire={tableau.partenaire} bons={tableau.bons || []} />;
  else if (chemin === '/coordonnees') {
    const retour = new URLSearchParams(window.location.search).get('retour') === 'commander' ? '/commander' : '/';
    ecran = <Coordonnees initiales={tableau.partenaire.coordonnees} retour={retour} onFait={charger} />;
  } else if (chemin === '/commander') ecran = <Commander onFait={async (ref) => { await charger(); naviguer(`/commande/${encodeURIComponent(ref)}`); }} />;
  else if (chemin.startsWith('/commande/')) {
    const ref = decodeURIComponent(chemin.slice(10));
    const c = (tableau.commandes || []).find((x) => x.ref === ref);
    ecran = c ? <DetailCommande c={c} /> : <Centre><div className="t-stone">Commande introuvable.</div></Centre>;
  } else if (chemin.startsWith('/envoi/')) {
    const ref = decodeURIComponent(chemin.slice(7));
    const e = tableau.envois.find((x) => x.ref === ref);
    ecran = e ? <DetailEnvoi envoi={e} onMaj={charger} /> : <Centre><div className="t-stone">Envoi introuvable.</div></Centre>;
  } else ecran = <Tableau data={tableau} />;

  return (
    <div className="proto-root" style={{ minHeight: '100vh', background: 'var(--creme)' }}>
      <Entete compte={compte} />
      {compte?.apercu ? (
        <div style={{ background: 'var(--jaune-pale)', borderBottom: '1px solid var(--bord)', fontSize: 13, padding: '8px 16px', textAlign: 'center' }}>
          <b>Aperçu Rejouis</b> : vous voyez l’espace de {compte.boutique} comme la boutique le voit. Lecture seule.{' '}
          <button className="btn btn-tertiary" style={{ padding: '2px 8px', fontSize: 13 }} onClick={() => { logout(); window.location.replace('/'); }}>Quitter l’aperçu</button>
        </div>
      ) : null}
      <main style={{ maxWidth: LARGEUR, margin: '0 auto', padding: '8px 16px 64px' }}>{ecran}</main>
    </div>
  );
}

function Centre({ children }) {
  return <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>{children}</div>;
}

function Entete({ compte }) {
  return (
    <header style={{ background: 'var(--blanc)', borderBottom: '1px solid var(--bord)' }}>
      <div style={{ maxWidth: LARGEUR, margin: '0 auto', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <a href="/" onClick={(e) => { e.preventDefault(); naviguer('/'); }} style={{ textDecoration: 'none' }}><Logo size={20} /></a>
        {compte ? (
          <>
            <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{compte.boutique}</span>
            <button className="btn btn-tertiary" style={{ padding: 8 }} title="Se déconnecter" aria-label="Se déconnecter"
              onClick={() => autreAdresse({ reconnecter: false })}><Icons.logout size={18} /></button>
          </>
        ) : null}
      </div>
    </header>
  );
}

function Connexion({ erreur }) {
  const [enCours, setEnCours] = useState(false);
  return (
    <div className="anim-pop" style={{ maxWidth: 440, margin: '48px auto 0', textAlign: 'center' }}>
      <div className="t-overline">Boutiques partenaires</div>
      <h1 className="t-display" style={{ fontSize: 32, margin: '8px 0 12px' }}>Votre espace pro</h1>
      <p className="t-stone" style={{ fontSize: 15, lineHeight: 1.6, margin: '0 0 28px' }}>
        Suivez les jouets que vous nous confiez : combien ont été reconditionnés, combien ont trouvé une nouvelle maison.
        Déclarez vos prochains envois et imprimez vos étiquettes.
      </p>
      <button className="btn btn-primary btn-block" disabled={enCours}
        onClick={async () => { setEnCours(true); try { await login(); } catch (e) { setEnCours(false); alert(e.message); } }}>
        {enCours ? <Spinner size={16} /> : 'Me connecter'}
      </button>
      <p className="t-stone" style={{ fontSize: 12.5, lineHeight: 1.6, marginTop: 14 }}>
        Avec le compte client rejouis.fr de la boutique : vous recevez un code par e-mail, pas de mot de passe.
      </p>
      <button className="btn btn-tertiary btn-block" onClick={() => autreAdresse()}>Me connecter avec une autre adresse e-mail</button>
      {erreur ? <p style={{ color: 'var(--rouge-sombre)', fontSize: 13 }}>{erreur}</p> : null}
    </div>
  );
}

function AccesRefuse({ email, onRetour }) {
  return (
    <div className="anim-pop card" style={{ maxWidth: 460, margin: '48px auto 0', padding: 28, textAlign: 'center' }}>
      <h1 className="t-display" style={{ fontSize: 24, margin: '0 0 10px' }}>Accès pas encore ouvert</h1>
      <p className="t-stone" style={{ fontSize: 14.5, lineHeight: 1.6, margin: 0 }}>
        Le compte <b style={{ color: 'var(--bleu)' }}>{email || 'utilisé'}</b> n’est relié à aucune boutique partenaire.
        Contactez votre interlocuteur Rejouis pour qu’il ouvre votre espace avec cette adresse.
      </p>
      <button className="btn btn-primary btn-block" style={{ marginTop: 20 }} onClick={() => autreAdresse()}>Me connecter avec une autre adresse e-mail</button>
      <button className="btn btn-tertiary btn-block" style={{ marginTop: 6 }} onClick={onRetour}>Retour</button>
    </div>
  );
}

// ── Tableau de bord ──
function Chiffre({ label, valeur, sous }) {
  return (
    <div style={{ flex: '1 1 140px' }}>
      <div className="t-overline">{label}</div>
      <div className="t-display" style={{ fontSize: 34 }}>{valeur.toLocaleString('fr-FR')}</div>
      {sous ? <div className="t-stone" style={{ fontSize: 12.5 }}>{sous}</div> : null}
    </div>
  );
}

// Statistiques par année d'envoi : un lot compte dans l'année où il a été déclaré, avec tout
// ce qu'il a donné depuis (reconditionnés, revendus).
const ANNEE_KEY = 'pro_annee';
const anneeDe = (e) => (e.date || '').slice(0, 4);
function lireAnnee() {
  try { return sessionStorage.getItem(ANNEE_KEY) || 'toutes'; } catch { return 'toutes'; }
}

function Tableau({ data }) {
  const { partenaire } = data;
  const commandesOuvertes = partenaire.commandesOuvertes !== false;
  const annees = [...new Set(data.envois.map(anneeDe).filter(Boolean))].sort().reverse();
  const [annee, setAnneeEtat] = useState(lireAnnee);
  const anneeActive = annee === 'toutes' || annees.includes(annee) ? annee : 'toutes';
  const setAnnee = (a) => { setAnneeEtat(a); try { sessionStorage.setItem(ANNEE_KEY, a); } catch { /* navigation privée */ } };
  const envois = anneeActive === 'toutes' ? data.envois : data.envois.filter((e) => anneeDe(e) === anneeActive);
  const totaux = envois.reduce((s, e) => ({
    recus: s.recus + (e.quantiteRecue || 0), reconditionnes: s.reconditionnes + (e.reconditionnes || 0), vendus: s.vendus + (e.vendus || 0),
  }), { recus: 0, reconditionnes: 0, vendus: 0 });
  const tReco = taux(totaux.reconditionnes, totaux.recus);
  const tVendus = taux(totaux.vendus, totaux.recus);
  return (
    <div className="anim-pop">
      <h1 className="t-display" style={{ fontSize: 28, margin: '18px 0 4px' }}>Bonjour {partenaire.nom}</h1>
      <p className="t-stone" style={{ margin: '0 0 18px', fontSize: 14.5 }}>Ce que deviennent les jouets que vous nous avez confiés.</p>

      {/* Coordonnées en tête : à remplir avant la première commande (bandeau rouge tant qu'elles manquent). */}
      {commandesOuvertes || partenaire.coordonneesCompletes ? <div className="card" style={{ padding: 16, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12,
        ...(partenaire.coordonneesCompletes ? {} : { borderColor: 'var(--rouge)', background: 'var(--rouge-pale)' }) }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="t-overline">Coordonnées de livraison</div>
          <div style={{ fontSize: 13.5, marginTop: 2, color: partenaire.coordonneesCompletes ? 'var(--encre-doux)' : 'var(--rouge-sombre)' }}>
            {partenaire.coordonneesCompletes ? resumeAdresse(partenaire.coordonnees) : 'À renseigner avant votre première commande.'}
          </div>
        </div>
        <button className={`btn ${partenaire.coordonneesCompletes ? 'btn-tertiary' : 'btn-primary'}`} style={{ padding: '10px 16px' }} onClick={() => naviguer('/coordonnees')}>
          {partenaire.coordonneesCompletes ? 'Modifier' : 'Renseigner'}
        </button>
      </div> : null}

      {annees.length > 1 ? (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }} role="group" aria-label="Année des envois">
          {['toutes', ...annees].map((a) => (
            <button key={a} className={`chip${a === anneeActive ? ' actif' : ''}`} onClick={() => setAnnee(a)}>
              {a === 'toutes' ? 'Toutes les années' : a}
            </button>
          ))}
        </div>
      ) : null}

      <div className="card" style={{ padding: 22, display: 'flex', gap: 18, flexWrap: 'wrap' }}>
        <Chiffre label={anneeActive === 'toutes' ? 'Jouets reçus' : `Jouets reçus en ${anneeActive}`} valeur={totaux.recus} sous={`${envois.length} envoi${envois.length > 1 ? 's' : ''}`} />
        <Chiffre label="Reconditionnés" valeur={totaux.reconditionnes} sous={tReco != null ? `${tReco} % des jouets reçus` : null} />
        <Chiffre label="Revendus" valeur={totaux.vendus} sous={tVendus != null ? `${tVendus} % des jouets reçus` : null} />
      </div>

      {partenaire.retrocom || partenaire.solde > 0 || data.bons?.length ? (
        <button className="card" onClick={() => naviguer('/bon-achat')}
          style={{ padding: 18, marginTop: 12, display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left', border: '1px solid var(--bord)' }}>
          <span style={{ color: 'var(--rouge)' }}><Icons.wallet size={26} /></span>
          <div style={{ flex: 1 }}>
            <div className="t-overline">Votre bon d’achat</div>
            <div className="t-stone" style={{ fontSize: 12.5 }}>
              {partenaire.retrocom ? `Taux actuel ${Math.round((partenaire.tauxRetrocom || 0.1) * 100)} % de vos jouets revendus, à` : 'À'} utiliser sur vos commandes · voir le détail
            </div>
          </div>
          <div className="t-display" style={{ fontSize: 26, color: 'var(--rouge)' }}>{fmtEuro(partenaire.solde)}</div>
          <span style={{ color: 'var(--stone)' }}><Icons.chev size={18} /></span>
        </button>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginTop: 18 }}>
        <button className="btn btn-primary" onClick={() => naviguer('/declarer')}>
          <Icons.truck size={16} /> Déclarer un envoi
        </button>
        {commandesOuvertes ? (
          <button className="btn btn-secondary" onClick={() => naviguer('/commander')}>
            <Icons.pack size={16} /> Commander des jouets
          </button>
        ) : null}
      </div>

      {data.commandes?.length ? (
        <>
          <h2 className="t-display" style={{ fontSize: 20, margin: '30px 0 12px' }}>Vos commandes</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.commandes.map((c) => <CarteCommande key={c.ref} c={c} />)}
          </div>
        </>
      ) : null}

      <h2 className="t-display" style={{ fontSize: 20, margin: '30px 0 12px' }}>Vos envois{anneeActive === 'toutes' ? '' : ` de ${anneeActive}`}</h2>
      {envois.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {envois.map((e) => <CarteEnvoi key={e.ref} e={e} />)}
        </div>
      ) : <div className="t-stone" style={{ fontSize: 14 }}>Pas encore d’envoi.</div>}

      <Faq retrocom={partenaire.retrocom} taux={partenaire.tauxRetrocom} commandes={commandesOuvertes} />
    </div>
  );
}

function CarteEnvoi({ e }) {
  const st = STATUTS[e.statut] || { label: e.statut, badge: 'badge-stone' };
  const recus = e.quantiteRecue ?? 0;
  return (
    <button className="card" onClick={() => naviguer(`/envoi/${encodeURIComponent(e.ref)}`)}
      style={{ display: 'block', width: '100%', textAlign: 'left', padding: 16, border: '1px solid var(--bord)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontWeight: 700, fontSize: 14.5 }}>{fmtDate(e.date) || e.ref}</span>
        <span className={`badge ${st.badge}`}>{st.label}</span>
        <span style={{ marginLeft: 'auto', color: 'var(--stone)' }}><Icons.chev size={18} /></span>
      </div>
      {aExpedier(e) ? (
        <div className="t-stone" style={{ fontSize: 13, marginTop: 6 }}>
          {e.quantiteDeclaree} jouets déclarés · {e.nbColis || 1} carton{(e.nbColis || 1) > 1 ? 's' : ''} : touchez pour vos étiquettes
        </div>
      ) : (
        <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
          <div className="t-stone" style={{ fontSize: 13 }}>
            {recus} reçus · <Pastille couleur="var(--bleu-clair)" /><b style={{ color: 'var(--bleu)' }}>{e.reconditionnes}</b> reconditionnés
            · <Pastille couleur="var(--bleu)" /><b style={{ color: 'var(--bleu)' }}>{e.vendus}</b> revendus
          </div>
          <Jauge valeur={e.reconditionnes} max={recus} couleur="var(--bleu-clair)" />
          <Jauge valeur={e.vendus} max={recus} />
        </div>
      )}
    </button>
  );
}

function Pastille({ couleur }) {
  return <span aria-hidden="true" style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 999, background: couleur, margin: '0 5px 1px 2px' }} />;
}

// ── Détail d'un envoi ──
function DetailEnvoi({ envoi: e, onMaj }) {
  const st = STATUTS[e.statut] || { label: e.statut, badge: 'badge-stone' };
  const recus = e.quantiteRecue ?? 0;
  const [envoiPhoto, setEnvoiPhoto] = useState(false);
  const [erreur, setErreur] = useState(null);

  async function ajouterPhotos(files) {
    setEnvoiPhoto(true); setErreur(null);
    try {
      for (const f of files) await pro('photo', { ref: e.ref, base64: await reduirePhoto(f) });
      await onMaj();
    } catch (err) { setErreur(err.message); }
    finally { setEnvoiPhoto(false); }
  }

  return (
    <div className="anim-pop">
      <Retour />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h1 className="t-display" style={{ fontSize: 26, margin: 0 }}>Envoi du {fmtDate(e.date)}</h1>
        <span className={`badge ${st.badge}`}>{st.label}</span>
      </div>
      <div className="t-stone mono" style={{ fontSize: 12, marginTop: 4 }}>{e.ref}</div>

      {aExpedier(e) ? (
        <Etiquettes e={e} onMaj={onMaj} />
      ) : (
        <div className="card" style={{ padding: 22, marginTop: 16 }}>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            <Chiffre label="Reçus" valeur={recus} sous={e.quantiteDeclaree && e.quantiteDeclaree !== recus ? `${e.quantiteDeclaree} déclarés` : null} />
            <Chiffre label="Reconditionnés" valeur={e.reconditionnes} sous={taux(e.reconditionnes, recus) != null ? `${taux(e.reconditionnes, recus)} %` : null} />
            <Chiffre label="Revendus" valeur={e.vendus} sous={taux(e.vendus, recus) != null ? `${taux(e.vendus, recus)} %` : null} />
          </div>
          {e.statut === 'Reçu' ? (
            <p className="t-stone" style={{ fontSize: 13, margin: '14px 0 0', lineHeight: 1.55 }}>
              Le reconditionnement est en cours : les chiffres avancent au fil des semaines.
            </p>
          ) : null}
          {e.tauxRetrocom ? (
            <p style={{ fontSize: 13.5, margin: '12px 0 0', lineHeight: 1.55, color: 'var(--bleu)' }}>
              Votre part sur les ventes de ce lot : <b>{Math.round(e.tauxRetrocom.taux * 100)} %</b>
              {e.tauxRetrocom.source === 'lot' ? ` (${Math.round(e.tauxRetrocom.ratio * 100)} % des jouets reçus reconditionnés)` : ' (taux de vos lots précédents, en attendant la fin du reconditionnement)'}.
            </p>
          ) : null}
        </div>
      )}

      {e.commentaire ? (
        <div className="card" style={{ padding: 18, marginTop: 12 }}>
          <div className="t-overline" style={{ marginBottom: 6 }}>Le mot de l’atelier</div>
          <div style={{ fontSize: 14.5, lineHeight: 1.6, whiteSpace: 'pre-line' }}>{e.commentaire}</div>
        </div>
      ) : null}

      {e.photosAtelier.length ? <Photos titre="Photos de l’atelier" photos={e.photosAtelier} /> : null}

      {e.testes || e.motBoutique ? (
        <div className="card" style={{ padding: 18, marginTop: 12 }}>
          <div className="t-overline" style={{ marginBottom: 6 }}>Votre description</div>
          {e.testes ? <div style={{ fontSize: 14 }}>Jouets testés : <b>{(TESTES.find(([v]) => v === e.testes) || [, e.testes])[1]}</b></div> : null}
          {e.motBoutique ? <div style={{ fontSize: 14.5, lineHeight: 1.6, whiteSpace: 'pre-line', marginTop: 6 }}>{e.motBoutique}</div> : null}
        </div>
      ) : null}

      <div className="card" style={{ padding: 18, marginTop: 12 }}>
        <div className="t-overline" style={{ marginBottom: 10 }}>Vos photos du lot</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {e.photosPartenaire.map((p) => <Vignette key={p.url} p={p} />)}
          <label className="chip" style={{ cursor: 'pointer' }}>
            {envoiPhoto ? <Spinner size={14} /> : <><Icons.camera size={16} /> Ajouter</>}
            <input type="file" accept="image/*" multiple hidden disabled={envoiPhoto} onChange={(ev) => ajouterPhotos([...ev.target.files])} />
          </label>
        </div>
        {erreur ? <div style={{ color: 'var(--rouge-sombre)', fontSize: 13, marginTop: 8 }}>{erreur}</div> : null}
      </div>
    </div>
  );
}

function Vignette({ p }) {
  return (
    <a href={p.url} target="_blank" rel="noopener noreferrer">
      <img src={p.thumb} alt="" loading="lazy" style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 12, border: '1px solid var(--bord)', display: 'block' }} />
    </a>
  );
}
function Photos({ titre, photos }) {
  return (
    <div className="card" style={{ padding: 18, marginTop: 12 }}>
      <div className="t-overline" style={{ marginBottom: 10 }}>{titre}</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{photos.map((p) => <Vignette key={p.url} p={p} />)}</div>
    </div>
  );
}

// Une étiquette par carton : QR à présenter en point relais, ou PDF de secours.
function Etiquettes({ e, onMaj }) {
  const [enCours, setEnCours] = useState(null);
  const [erreur, setErreur] = useState(null);
  const nb = e.etiquettes.length;

  async function pdf(colis) {
    if (!window.confirm('Créer une étiquette à imprimer pour ce carton ?\n\nSon QR code ne sera alors plus valable.')) return;
    setEnCours(colis); setErreur(null);
    try { await pro('etiquette_pdf', { ref: e.ref, colis }); await onMaj(); }
    catch (err) { setErreur(err.message); }
    finally { setEnCours(null); }
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div className="card" style={{ padding: 18 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <span style={{ color: 'var(--rouge)', marginTop: 2 }}><Icons.truck size={24} /></span>
          <div style={{ fontSize: 14, lineHeight: 1.6 }}>
            <b>{e.quantiteDeclaree} jouets en {nb || e.nbColis} carton{(nb || e.nbColis) > 1 ? 's' : ''}.</b> Déposez chaque carton dans
            n’importe quel point relais Mondial Relay avec son QR code : le relais imprime l’étiquette.{' '}
            <a href={CARTE_RELAIS} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--rouge)', fontWeight: 600 }}>Trouver un relais</a>
          </div>
        </div>
      </div>
      {nb === 0 ? (
        <div className="card" style={{ padding: 18, marginTop: 10, color: 'var(--rouge-sombre)', fontSize: 14 }}>
          Les étiquettes n’ont pas pu être créées. Contactez votre interlocuteur Rejouis.
        </div>
      ) : null}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 10, marginTop: 10 }}>
        {e.etiquettes.map((t) => (
          <div key={t.colis} className="card" style={{ padding: 16, textAlign: 'center' }}>
            <div style={{ fontWeight: 800, fontSize: 15 }}>Carton {t.colis}{nb > 1 ? ` sur ${nb}` : ''}</div>
            {t.suivi?.etape > 0 ? <div className="badge badge-mint" style={{ marginTop: 6 }}>{ETAPES_COLIS[t.suivi.etape]}</div> : null}
            {t.qr_code ? (
              <>
                <img src={`/api/qr?d=${encodeURIComponent(t.qr_code)}`} alt={`QR code du carton ${t.colis}`} width={200} height={200}
                  style={{ width: 200, height: 200, display: 'block', margin: '10px auto', background: '#fff', borderRadius: 12, border: '1px solid var(--bord)' }} />
                <button className="btn btn-tertiary btn-block" style={{ fontSize: 10.5 }} disabled={enCours != null} onClick={() => pdf(t.colis)}>
                  {enCours === t.colis ? <Spinner size={14} /> : 'Relais sans QR code : étiquette à imprimer'}
                </button>
              </>
            ) : t.bordereau ? (
              <a className="btn btn-primary btn-block" style={{ marginTop: 10, textDecoration: 'none' }} href={t.bordereau} target="_blank" rel="noopener noreferrer">
                <Icons.download size={16} /> Imprimer l’étiquette
              </a>
            ) : null}
            {t.suivi_url ? (
              <a href={t.suivi_url} target="_blank" rel="noopener noreferrer" className="t-stone" style={{ fontSize: 12, display: 'inline-flex', gap: 4, alignItems: 'center', marginTop: 6 }}>
                Suivi {t.tracking} <Icons.external size={12} />
              </a>
            ) : null}
          </div>
        ))}
      </div>
      {erreur ? <div style={{ color: 'var(--rouge-sombre)', fontSize: 13, marginTop: 8 }}>{erreur}</div> : null}
    </div>
  );
}

// ── Déclarer un envoi ──
function Compteur({ label, aide, valeur, setValeur, min = 1, max }) {
  const borne = (v) => Math.max(min, Math.min(max, Math.round(Number(v) || min)));
  return (
    <div className="card" style={{ padding: 18 }}>
      <div style={{ fontWeight: 700, fontSize: 15 }}>{label}</div>
      {aide ? <div className="t-stone" style={{ fontSize: 13, margin: '2px 0 0' }}>{aide}</div> : null}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
        <button className="chip" style={{ width: 44, height: 44, justifyContent: 'center', fontSize: 20 }} onClick={() => setValeur(borne(valeur - 1))} aria-label="Moins">−</button>
        <input className="field" type="number" inputMode="numeric" min={min} max={max} value={valeur}
          onChange={(ev) => setValeur(ev.target.value === '' ? '' : borne(ev.target.value))}
          style={{ width: 110, textAlign: 'center', fontSize: 20, fontWeight: 700 }} />
        <button className="chip" style={{ width: 44, height: 44, justifyContent: 'center', fontSize: 20 }} onClick={() => setValeur(borne((Number(valeur) || 0) + 1))} aria-label="Plus">+</button>
      </div>
    </div>
  );
}

const TESTES = [
  ['Tous testés', 'Oui, tous'],
  ['Une partie', 'Une partie'],
  ['Non testés', 'Non'],
];
const MAX_PHOTOS = 6;

function Declarer({ onFait }) {
  const [quantite, setQuantite] = useState(20);
  const [nbColis, setNbColis] = useState(1);
  const [photos, setPhotos] = useState([]); // [{ file, apercu }]
  const [testes, setTestes] = useState('');
  const [mot, setMot] = useState('');
  const [enCours, setEnCours] = useState(null);
  const [erreur, setErreur] = useState(null);
  const pret = quantite && nbColis && photos.length && testes;

  function ajouter(files) {
    const nouvelles = [...files].slice(0, MAX_PHOTOS - photos.length).map((file) => ({ file, apercu: URL.createObjectURL(file) }));
    setPhotos((p) => [...p, ...nouvelles]);
  }

  async function valider() {
    setEnCours('Création des étiquettes…'); setErreur(null);
    let ref;
    try {
      ({ envoi: { ref } } = await pro('declarer', { quantite: Number(quantite), nbColis: Number(nbColis), testes, mot }));
    } catch (e) { setErreur(e.message); setEnCours(null); return; }
    // Envoi créé : les photos suivent une à une (une photo ratée n'annule pas l'envoi).
    let ratees = 0;
    for (let i = 0; i < photos.length; i++) {
      setEnCours(`Envoi des photos (${i + 1}/${photos.length})…`);
      try { await pro('photo', { ref, base64: await reduirePhoto(photos[i].file) }); } catch { ratees++; }
    }
    if (ratees) window.alert(`${ratees} photo(s) n’ont pas pu être envoyées : vous pourrez les ajouter depuis la page de l’envoi.`);
    await onFait(ref);
  }

  return (
    <div className="anim-pop">
      <Retour />
      <h1 className="t-display" style={{ fontSize: 26, margin: '0 0 6px' }}>Déclarer un envoi</h1>
      <p className="t-stone" style={{ fontSize: 14.5, lineHeight: 1.6, margin: '0 0 16px' }}>
        Indiquez combien de jouets vous nous envoyez et dans combien de cartons : vous recevez un QR code Mondial Relay
        par carton, à présenter en point relais.
      </p>
      <div style={{ display: 'grid', gap: 10 }}>
        <Compteur label="Nombre de jouets" aide="Une estimation suffit : on recompte à l’arrivée." valeur={quantite} setValeur={setQuantite} max={2000} />
        <Compteur label="Nombre de cartons" aide="30 kg au maximum par carton (limite des points relais)." valeur={nbColis} setValeur={setNbColis} max={10} />

        <div className="card" style={{ padding: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Photo du lot</div>
          <div className="t-stone" style={{ fontSize: 13, margin: '2px 0 0' }}>Les jouets étalés, avant de fermer le carton.</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            {photos.map((p, i) => (
              <div key={p.apercu} style={{ position: 'relative' }}>
                <img src={p.apercu} alt="" style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 12, border: '1px solid var(--bord)', display: 'block' }} />
                <button aria-label="Retirer la photo" onClick={() => setPhotos((l) => l.filter((_, j) => j !== i))}
                  style={{ position: 'absolute', top: -6, right: -6, width: 24, height: 24, borderRadius: 999, border: 'none', background: 'var(--bleu)', color: '#fff', fontSize: 14, lineHeight: '24px', cursor: 'pointer' }}>×</button>
              </div>
            ))}
            {photos.length < MAX_PHOTOS ? (
              <label className="chip" style={{ cursor: 'pointer', height: 84, minWidth: 84, justifyContent: 'center', flexDirection: 'column', gap: 4, borderStyle: 'dashed', borderRadius: 12 }}>
                <Icons.camera size={20} /><span style={{ fontSize: 12 }}>Ajouter</span>
                <input type="file" accept="image/*" multiple hidden onChange={(ev) => { ajouter(ev.target.files); ev.target.value = ''; }} />
              </label>
            ) : null}
          </div>
        </div>

        <div className="card" style={{ padding: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Avez-vous testé les jouets ?</div>
          <div className="t-stone" style={{ fontSize: 13, margin: '2px 0 0' }}>Allumés, chargés, en état de marche.</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            {TESTES.map(([val, label]) => (
              <button key={val} className={`chip${testes === val ? ' actif' : ''}`} style={{ padding: '10px 18px', fontSize: 14 }} onClick={() => setTestes(val)}>{label}</button>
            ))}
          </div>
        </div>

        <div className="card" style={{ padding: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Un petit mot sur le lot</div>
          <div className="t-stone" style={{ fontSize: 13, margin: '2px 0 0' }}>Ce qu’il contient, d’où il vient, les jouets neufs ou à surveiller…</div>
          <textarea className="field" rows={4} maxLength={2000} value={mot} onChange={(ev) => setMot(ev.target.value)} style={{ marginTop: 12 }}
            placeholder="Ex. retours clients de septembre, 4 jouets encore scellés, 2 rabbits qui ne chargent plus." />
        </div>
      </div>
      <button className="btn btn-primary btn-block" style={{ marginTop: 18 }} disabled={enCours || !pret} onClick={valider}>
        {enCours ? <Spinner size={16} label={enCours} /> : `Obtenir ${Number(nbColis) > 1 ? `mes ${nbColis} étiquettes` : 'mon étiquette'}`}
      </button>
      {!pret && !enCours ? (
        <div className="t-stone" style={{ fontSize: 12.5, textAlign: 'center', marginTop: 8 }}>
          {!photos.length ? 'Ajoutez au moins une photo du lot' : 'Dites-nous si les jouets ont été testés'} pour obtenir vos étiquettes.
        </div>
      ) : null}
      {erreur ? <div style={{ color: 'var(--rouge-sombre)', fontSize: 13, marginTop: 10 }}>{erreur}</div> : null}
    </div>
  );
}
